import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, List, Avatar } from "antd";
import { downloadApplicationZip } from '../../../../../store/forms/actions';
import { formatBytes, getFileImage, getFullDateHours } from "utils/helpers";
import { ReactComponent as DownloadFile } from 'assets/icons/download_blue.svg';
import { downloadUploadedFile, openUploadedFile } from 'store/forms/actions';
import { isAuditor, isCommittee, isBeneficiary } from 'utils';
import { useLocation } from 'react-router-dom'; import { printInfo } from 'store/appTrack/utils';
import { content, content_pea, content_optional } from 'pages/mainForms/mainForm/components/documentsBeneficiary/components/docsInvestor/content';

// Document order configuration
const DOCUMENT_ORDER = [
  'benApplAuthor',
  'benPropStatus', 
  'benBuildLegal',
  'benRespDeclar',
  'benPrimEarth',
  'benTechStudy',
  'benCompSub',
  'benDecision',
  'benSeak',
  'benFirstPea',
  'benCoverageDoc',
  'benOther',
  'otherDocs'
];

// Create title to fileCode mapping from content arrays
const createTitleToFileCodeMap = () => {
  const allContent = [...content, ...content_pea, ...content_optional];
  const titleMap = {};
  
  allContent.forEach(item => {
    titleMap[item.label] = item.fileCode;
  });
  
  return titleMap;
};

// Function to sort documents by desired order
const sortDocumentsByOrder = (applicationFilesList) => {
  if (!applicationFilesList || !applicationFilesList.length) {
    return applicationFilesList;
  }
  
  const titleToFileCodeMap = createTitleToFileCodeMap();
  
  return [...applicationFilesList].sort((a, b) => {
    const fileCodeA = titleToFileCodeMap[a.title];
    const fileCodeB = titleToFileCodeMap[b.title];
    
    const orderA = fileCodeA ? DOCUMENT_ORDER.indexOf(fileCodeA) : 999;
    const orderB = fileCodeB ? DOCUMENT_ORDER.indexOf(fileCodeB) : 999;
    
    // If both have valid orders, sort by order
    if (orderA !== -1 && orderB !== -1) {
      return orderA - orderB;
    }
    
    // If only one has a valid order, prioritize it
    if (orderA !== -1) return -1;
    if (orderB !== -1) return 1;
    
    // If neither has a valid order, maintain original order
    return 0;
  });
};

const Documents = () => {
  const dispatch = useDispatch();
  const ApplicationFilesList = useSelector(state=> state.appTrack?.apptrackDetails?.contentItems);
  const ApplicationFilesListMainforms = useSelector(state => state.forms?.documents?.contentItems);
  const groups = useSelector(state => state.user?.groups);
  const location = useLocation();
  const appTrack = location.pathname.includes('apptrack');

  const apptrackDetails = useSelector(state => state.appTrack.apptrackDetails);
  const businessKey = useSelector(state => state.appTrack?.apptrackDetails?.businessKey);

  const response = Object.keys(apptrackDetails?.variablesIndexed || {}).length === 1 && apptrackDetails?.variablesIndexed.hasOwnProperty('initiator')
    ? apptrackDetails?.localTaskVariables
    : apptrackDetails?.variablesIndexed;

  // Get the appropriate data source and sort it
  const rawDataSource = !isBeneficiary(groups) ? 
    appTrack ? ApplicationFilesList : ApplicationFilesListMainforms
    : ApplicationFilesList;
  
  const sortedDataSource = sortDocumentsByOrder(rawDataSource);

  const handleMassDownloadZip = async () => {
    if (!businessKey) {
        console.warn("Business key missing. Cannot download ZIP.");
        return;
      }
        try {
          console.warn("Business key: ");
          console.warn(businessKey);
          await dispatch(downloadApplicationZip(businessKey));
          console.log("ZIP download triggered!");
        } catch (err) {
          console.error("Failed to download ZIP:", err);
        }
    };

  // Check if user can see download all button
  const canMassDownload = isCommittee(groups) || isAuditor(groups);

  return (
    <Row gutter={16}>
      <Col span={24}>
        {canMassDownload && (
          <div
            className="mass-download-wrapper"
            style={{ marginBottom: 16, display: "flex", justifyContent: "flex-end" }}
          >
            <a
              className="application-download-file-text"
              onClick={(e) => {
                e.preventDefault();
                handleMassDownloadZip();
              }}
            >
              <DownloadFile /> Download All Files
            </a>
          </div>
        )}
        <List
          className="applicationFilesList no-border"
          itemLayout="vertical"
          dataSource={sortedDataSource}
          renderItem={(item, index) => (           
            <div key={index}>
               <div className="application-file-header">{item?.title}</div>
            <List.Item
              extra={
                item.items.map((info, infoIndex) => (
                <div className="file-uris" key={infoIndex}>
                  <div className="open-file-wrapper file-actions">
                    <a className="application-open-file-text" 
                      onClick={(e) => {
                      e.preventDefault();
                      dispatch(openUploadedFile(info))
                    }}
                    target={"_blank"}>Άνοιγμα</a>
                  </div> 
                  <div className="file-actions">
                      <a className="application-download-file-text" 
                      onClick={(e) => {
                        e.preventDefault();
                        dispatch(downloadUploadedFile(info))
                      }}
                      download={info.name}>
                      <DownloadFile/> Download</a>
                  </div>
                </div>
                ))
              }
            >
                {item.items.map((info, infoIndex) => ( 
                    <List.Item.Meta className={infoIndex == 0 ? "first" : ""} key={infoIndex}
                    avatar={<Avatar src={getFileImage(info.simpleType)} />}
                    title={
                      <div style={{display: "flex"}}>
                        <span className="application-file-name">{info.name}</span>
                      </div>
                      }
                  description={
                  <div>
                  <span className="application-file-size">{formatBytes(info.size)}</span><span className='application-file-size'>, Updated: {getFullDateHours(info.created)}</span>
                   {item.title=="1ο Πιστοποιητικό Ενεργειακής Απόδοσης" &&
                    (<div>
                      <div  style={{marginTop: '0.5rem'}}>
                        <span className="meta-tag mr-1"><span className="bold">Αρ. Πρωτοκόλλου ΠΕΑ </span> {printInfo(response?.prclNumber)} </span>
                        <span className="meta-tag mr-1"> <span className="bold">Κωδικός Ασφαλείας ΠΕΑ </span>{printInfo(response?.secCode)} </span>
                        <span className="meta-tag mr-1"><span className="bold">Έτος ΠΕΑ </span>{printInfo(response?.certificationYear)} </span>
                      </div>
                      <div style={{marginTop: '0.5rem'}}>
                        <span className="meta-tag mr-1"><span className="bold">Υπολ. Ετήσιας Κατανάλωσης Πρωτ. Ενέργειας </span> {printInfo(response?.energyConsumption)} </span>
                        <span className="meta-tag mr-1"> <span className="bold">Υπολ. Ετήσιες Εκπομπές CO2 </span>{printInfo(response?.reductionCo2)} </span>
                      </div>
                      </div>)
                    }
                  </div>}
                 
                  />
                ))}
                
            </List.Item> 
            </div>
          )}
        />
      </Col>
    </Row>
  );
};

export default Documents;